import { useState } from 'react';
import AiMarkdown from '../../../ai/AiMarkdown';
import { Badge, Button, Card, Dropdown, Input, KebabMenu, useToast } from '../../../ui';
import type { BadgeTone } from '../../../ui';
import {
  JOURNAL_KINDS,
  addJournalEntry,
  deleteJournalEntry,
  updateJournalEntry,
  type JournalDraft,
  type JournalEntry,
  type JournalKind,
} from '../../../../lib/googleAds';
import { formatDate } from '../../../../utils/dateUtils';

interface AdsJournalProps {
  clientId: string;
  entries: JournalEntry[];
  onChanged: () => void;
}

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
const blank = (): JournalDraft => ({ entry_date: today(), kind: 'change', title: '', body: '', review_by: null });
const kindOf = (id: JournalKind) => JOURNAL_KINDS.find((k) => k.id === id) ?? JOURNAL_KINDS[JOURNAL_KINDS.length - 1];
const label: React.CSSProperties = { fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' };
const day = (d: string) => formatDate(`${d}T12:00:00`);

/**
 * The account's running journal: what changed and why, what a read showed, what was decided, what someone else
 * did to the account, and when to look again (review_by). Agents write the same rows with `cockpit ads journal add`.
 */
export default function AdsJournal({ clientId, entries, onChanged }: AdsJournalProps) {
  const toast = useToast();
  const [composing, setComposing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<JournalDraft>(blank());
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<'all' | JournalKind>('all');

  const startNew = () => {
    setEditingId(null);
    setDraft(blank());
    setComposing(true);
  };
  const startEdit = (e: JournalEntry) => {
    setEditingId(e.id);
    setDraft({ entry_date: e.entry_date, kind: e.kind, title: e.title, body: e.body ?? '', review_by: e.review_by });
    setComposing(true);
  };

  const save = async () => {
    if (!draft.title.trim()) return;
    setSaving(true);
    const clean = { ...draft, title: draft.title.trim(), body: draft.body?.trim() || null, review_by: draft.review_by || null };
    try {
      if (editingId) await updateJournalEntry(editingId, clean);
      else await addJournalEntry(clientId, clean);
      setComposing(false);
      setEditingId(null);
      onChanged();
    } catch (err) {
      console.error('Error saving journal entry:', err);
      toast.error('Couldn’t save the entry', err instanceof Error ? err.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn: () => Promise<void>, failure: string) => {
    try {
      await fn();
      onChanged();
    } catch (err) {
      console.error(failure, err);
      toast.error(failure, err instanceof Error ? err.message : undefined);
    }
  };

  const shown = filter === 'all' ? entries : entries.filter((e) => e.kind === filter);
  const t = today();

  return (
    <Card
      eyebrow="Journal"
      title="What we did and why"
      actions={
        <>
          <Dropdown
            size="sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | JournalKind)}
            options={[{ value: 'all', label: 'All kinds' }, ...JOURNAL_KINDS.map((k) => ({ value: k.id, label: k.label }))]}
          />
          {composing ? null : <Button size="sm" variant="primary" icon="plus" onClick={startNew}>Entry</Button>}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {composing ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              padding: 'var(--space-4)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface-raised)',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
              <div>
                <span style={label}>Date</span>
                <input type="date" className="ck-date-input" value={draft.entry_date} onChange={(e) => setDraft({ ...draft, entry_date: e.target.value })} style={{ width: 160 }} />
              </div>
              <Dropdown
                label="Kind"
                size="sm"
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value as JournalKind })}
                options={JOURNAL_KINDS.map((k) => ({ value: k.id, label: k.label }))}
                style={{ minWidth: 170 }}
              />
              <div>
                <span style={label}>Review by (optional)</span>
                <input type="date" className="ck-date-input" value={draft.review_by ?? ''} onChange={(e) => setDraft({ ...draft, review_by: e.target.value || null })} style={{ width: 160 }} />
              </div>
            </div>
            <Input label="Title" fieldSize="sm" value={draft.title} placeholder="Paused Colorado general; budget to Signal" onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            <div>
              <span style={label}>Details: what changed, why, expected effect, what to watch, how to undo (Markdown)</span>
              <textarea className="ck-textarea" rows={6} value={draft.body ?? ''} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
              <Button size="sm" variant="ghost" onClick={() => setComposing(false)}>Cancel</Button>
              <Button size="sm" variant="primary" onClick={save} disabled={saving || !draft.title.trim()}>
                {saving ? 'Saving…' : editingId ? 'Save' : 'Add entry'}
              </Button>
            </div>
          </div>
        ) : null}

        {shown.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            {entries.length === 0 ? 'No entries yet.' : 'No entries of that kind.'}
          </p>
        ) : (
          shown.map((e) => {
            const kind = kindOf(e.kind);
            const due = e.review_by && !e.reviewed_at && e.review_by <= t;
            const who = e.creator?.name ?? e.author;
            return (
              <div key={e.id} style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingBottom: 'var(--space-4)', borderBottom: '1px solid var(--border-hairline)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{day(e.entry_date)}</span>
                  <Badge tone={kind.tone as BadgeTone}>{kind.label}</Badge>
                  {e.review_by ? (
                    e.reviewed_at ? (
                      <Badge tone="idle">reviewed</Badge>
                    ) : (
                      <Badge tone={due ? 'warn' : 'neutral'}>{due ? 'review due' : 'review'} {day(e.review_by)}</Badge>
                    )
                  ) : null}
                  <span style={{ flex: 1 }} />
                  {who ? <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>{who}</span> : null}
                  <KebabMenu
                    label="Entry actions"
                    actions={[
                      { label: 'Edit', onClick: () => startEdit(e) },
                      e.review_by && !e.reviewed_at
                        ? { label: 'Mark reviewed', onClick: () => act(() => updateJournalEntry(e.id, { reviewed_at: new Date().toISOString() }), 'Couldn’t mark it reviewed') }
                        : null,
                      { label: 'Delete', danger: true, onClick: () => act(() => deleteJournalEntry(e.id), 'Couldn’t delete the entry') },
                    ]}
                  />
                </div>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-body)' }}>{e.title}</p>
                {e.body ? <AiMarkdown content={e.body} /> : null}
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
